import React from 'react';
import { Card, Row, Col, Button, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useOwnerBusinessHealth } from '@/hooks/useOwnerIntelligence';
import {
  HeroKpiCard,
  InsightActionCard,
  SignalBadge,
  DataCoverageDots,
  OiPageFrame,
  OiMetricHelp,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { scoreToSignalTier, signalTierColor } from '@/components/owner-intelligence/oi-signals';
import { OiHorizontalBarChart, OiRadialGauge } from '@/components/owner-intelligence/charts/OiCharts';

const BusinessHealthPage: React.FC = () => {
  const { data, isLoading, error } = useOwnerBusinessHealth();
  const navigate = useNavigate();

  return (
    <OiPageFrame
      subtitle="Operational health scores across financial, delivery, and risk dimensions (exception-based, not profit)."
      loading={isLoading && !data}
      error={Boolean(error || !data)}
      errorMessage="Business health unavailable"
    >
      {data ? (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Looking for P&L and balance sheet?"
            action={
              <Button size="small" type="primary" onClick={() => navigate('/owner-intelligence/financial-health')}>
                Open Financial Health
              </Button>
            }
            description="This page scores operational exceptions. For comparative profit, cash, and balance sheet by fiscal year, use Financial Health."
          />
          <Card style={{ marginBottom: 16 }} bordered={false}>
            <Row align="middle" gutter={24}>
              <Col xs={24} md={8}>
                <HeroKpiCard
                  label="Overall Operational Health"
                  value={`${data.composite_score}%`}
                  score={data.composite_score}
                  statusTier={scoreToSignalTier(data.composite_score)}
                />
                <OiMetricHelp title="Operational health score">
                  Weighted average of dimension scores derived from open ERP exceptions (overdue invoices, delayed WOs, etc.).
                  Not a financial statement metric.
                </OiMetricHelp>
              </Col>
              <Col xs={24} md={16}>
                <div style={{ marginBottom: 8, fontWeight: 600 }}>Top drivers (by impact)</div>
                <OiHorizontalBarChart
                  data={data.top_drivers.map((d) => ({
                    name: `#${d.rank} ${d.message.slice(0, 40)}`,
                    impact: Math.max(1, 10 - d.rank),
                  }))}
                  dataKey="impact"
                  nameKey="name"
                  height={Math.min(280, data.top_drivers.length * 36 + 40)}
                />
              </Col>
            </Row>
          </Card>

          <Row gutter={[16, 16]}>
            {data.dimensions.map((dim) => {
              const tier = scoreToSignalTier(dim.score);
              const isCritical = dim.score < 70;
              return (
                <Col xs={24} sm={12} lg={6} key={dim.key}>
                  <Card
                    bordered={false}
                    title={dim.label}
                    extra={<SignalBadge tier={tier} />}
                    style={{ borderLeft: `3px solid ${signalTierColor(tier)}` }}
                    className={isCritical ? 'oi-kpi-card--critical-pulse' : undefined}
                  >
                    <OiRadialGauge score={dim.score} />
                    <DataCoverageDots value={dim.confidence} label={`${dim.drivers?.length ?? 0} open drivers`} />
                    <div style={{ marginTop: 8, fontSize: 12, color: 'var(--oi-text-muted)' }}>
                      Target: {dim.target}%
                    </div>
                    {dim.recommended_actions[0] ? (
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0, marginTop: 8 }}
                        onClick={
                          dim.recommended_actions[0].linked_route
                            ? () => navigate(dim.recommended_actions[0].linked_route!)
                            : undefined
                        }
                      >
                        Take action →
                      </Button>
                    ) : null}
                  </Card>
                </Col>
              );
            })}
          </Row>

          {data.recommended_actions.length > 0 ? (
            <Card title="Recommended actions" style={{ marginTop: 16 }} bordered={false}>
              <Row gutter={[16, 16]}>
                {data.recommended_actions.map((a, i) => (
                  <Col xs={24} md={12} lg={8} key={i}>
                    <InsightActionCard
                      title={a.title}
                      description={a.description}
                      priority={a.priority}
                      onAction={a.linked_route ? () => navigate(a.linked_route!) : undefined}
                    />
                  </Col>
                ))}
              </Row>
            </Card>
          ) : null}
        </>
      ) : null}
    </OiPageFrame>
  );
};

export default BusinessHealthPage;
